// =====================================================================
// commande-prete/vue.js — Commandes prêtes v3 (16-10-2026) — VUE D'ENSEMBLE (page /commande-prete)
// =====================================================================
// SPEC v3 A1 : filtres client (« Tous les clients ») + jour (Aujourd'hui / Hier / 7 derniers jours),
// 4 compteurs cliquables = 4 vues, vue par défaut « Encore à quai » ; temps à quai « 2 j 4 h ».
// Réponse du serveur (GET /api/ready-orders/vue) : { compteurs: { envoyees, brouillons, quai,
// chargees }, envoyees: [ligne], brouillons: [ligne], quai: [ligne], chargees: [ligne] } — ligne :
// { id, orderRef, client{id,name}, isTest, statut, envoyeLe (1er mail), dernierMail, creeLe, palettes, colis,
//   unites, charges, etatChargement, dernierChargement, aQuaiDepuis, aQuaiJusqua, dureeQuaiMinutes, rdv,
//   aCorriger, supprimable? (brouillons), facturation: null | { aReprendre, motifs } } ; + aFacturer : [ligne]
//   (facturation automatique à reprendre, toutes dates — montrée aux admins).
// Fonctions PURES (aucun DOM) : testées par frontend/test/commande-prete.test.mjs.
// =====================================================================

import { dateHeureBxl } from './dates.js';
import { jourCourt, demiJournee } from './rdvDocs.js';

export const VUES = ['envoyees', 'brouillons', 'quai', 'chargees'];
export const VUE_DEFAUT = 'quai';
export const JOURS = [
  { v: 0, txt: 'Aujourd\'hui' },
  { v: 1, txt: 'Hier' },
  { v: 7, txt: '7 derniers jours' },
];
export const JOUR_DEFAUT = 0;
// Entrée « Tous les clients » du champ Client du filtre (ChoixClient) : id vide = pas de filtre.
export const TOUS_LES_CLIENTS = Object.freeze({ id: '', name: 'Tous les clients' });

/** Vue demandée → une des 4 (sinon la vue par défaut). */
export const lireVue = (v) => (VUES.includes(v) ? v : VUE_DEFAUT);
/** Jour demandé → 0, 1 ou 7 (sinon aujourd'hui). */
export const lireJour = (v) => (JOURS.some((j) => j.v === Number(v)) && v !== '' && v !== null ? Number(v) : JOUR_DEFAUT);

/** Paramètres de GET /vue : client choisi (sauf « Tous les clients ») et jour. */
export function paramsVue({ client, jour } = {}) {
  return { ...(client && client.id ? { clientId: client.id } : {}), jour: lireJour(jour) };
}

const periode = (jour) => ({ 0: 'aujourd\'hui', 1: 'hier', 7: 'ces 7 derniers jours' }[lireJour(jour)]);

/** Libellé d'un compteur (n = nombre) — le filtre jour ne vaut pas pour « brouillons » ni « à quai ». */
export function libelleCompteur(vue, n, jour) {
  const k = Number(n) || 0;
  const s = k > 1 ? 's' : '';
  switch (vue) {
    case 'envoyees': return `envoyée${s} ${periode(jour)}`;
    case 'brouillons': return `brouillon${s}`;
    case 'quai': return `commande${s} encore à quai`;
    case 'chargees': return `chargée${s} ${periode(jour)}`;
    default: return '';
  }
}

/** Phrase sous les compteurs : ce que montre la vue choisie. */
export function explication(vue) {
  return {
    envoyees: 'Mail « commande prête » parti dans la période (le filtre Jour change la période). Commandes de test : dans « Brouillons ».',
    brouillons: 'Commencées, jamais envoyées, et toutes les commandes de TEST. Seules celles-ci se suppriment.',
    quai: 'Toutes les commandes envoyées dont au moins une palette ou un colis n\'est pas encore dans un camion, quelle que soit la date.',
    chargees: 'Chargées (tout ou partie) dans la période, avec l\'heure du dernier scan et le temps passé à quai.',
  }[lireVue(vue)];
}

/** Rien dans la vue : le dire (jamais une liste vide muette). */
export function texteVide(vue, { client, jour } = {}) {
  const qui = client && client.id ? ` pour ${client.name}` : '';
  return {
    envoyees: `Aucune commande envoyée ${periode(jour)}${qui}.`,
    brouillons: `Aucun brouillon ni commande de test${qui}.`,
    quai: `Rien à quai${qui} : tout ce qui a été envoyé est chargé.`,
    chargees: `Rien de chargé ${periode(jour)}${qui}.`,
  }[lireVue(vue)];
}

/** Temps à quai : « 2 j 4 h », « 4 h », « < 1 h » ; inconnu → « — ». */
export function texteDuree(minutes) {
  if (minutes === null || minutes === undefined || !Number.isFinite(Number(minutes)) || Number(minutes) < 0) return '—';
  const m = Math.floor(Number(minutes));
  const j = Math.floor(m / 1440);
  const h = Math.floor((m % 1440) / 60);
  if (j) return `${j} j ${h} h`;
  if (h) return `${h} h`;
  return '< 1 h';
}

/**
 * Envoi d'une ligne : « envoyée le 16/10 09:12 » (+ « correction le … » si un mail est reparti depuis) ;
 * 1er envoi pas encore tranché : « 1er envoi à vérifier » / « 1er envoi en cours ».
 */
export function texteEnvoi(l) {
  if (!l || !l.envoyeLe) {
    if (l && l.statut === 'A_VERIFIER') return '1er envoi à vérifier (le mail est peut-être parti)';
    if (l && l.statut === 'ENVOI_EN_COURS') return '1er envoi en cours';
    return 'jamais envoyée';
  }
  const t = `envoyée le ${dateHeureBxl(l.envoyeLe)}`;
  const d = l.dernierMail ? new Date(l.dernierMail).getTime() : NaN;
  return Number.isFinite(d) && d > new Date(l.envoyeLe).getTime() + 60_000 ? `${t} · correction le ${dateHeureBxl(l.dernierMail)}` : t;
}

/** Unités d'une ligne : « 2 pal. + 1 colis » (ou « aucune unité »). */
export function texteUnitesVue(l) {
  const t = [];
  if (l.palettes) t.push(`${l.palettes} pal.`);
  if (l.colis) t.push(`${l.colis} colis`);
  return t.join(' + ') || 'aucune unité';
}

/** Reste à quai : « 2 / 3 ». */
export const texteReste = (l) => `${Math.max(0, (Number(l.unites) || 0) - (Number(l.charges) || 0))} / ${Number(l.unites) || 0}`;

/** État de chargement → { txt, cls } (pastille). */
export function etatVue(l) {
  if (l.isTest) return { txt: 'TEST', cls: 'test' };
  switch (l.etatChargement) {
    case 'CHARGEE': return { txt: 'Chargée', cls: 'ok' };
    case 'PARTIELLE': return { txt: `Partiellement chargée (${l.charges}/${l.unites})`, cls: 'part' };
    case 'VIDE': return { txt: 'Aucune unité', cls: 'draft' };
    default: return { txt: 'À quai', cls: 'quai' };
  }
}

/** Statut d'envoi d'une ligne de brouillon → pastille. */
export function statutBrouillon(l) {
  if (l.statut === 'A_VERIFIER') return { txt: '⚠ envoi à vérifier', cls: 'warn' };
  if (l.statut === 'ENVOI_EN_COURS') return { txt: '⏳ envoi en cours', cls: 'part' };
  if (l.envoyeLe) return { txt: 'test envoyé', cls: 'test' };
  if (l.complete) return { txt: 'prête à envoyer', cls: 'part' };
  return { txt: 'brouillon', cls: 'draft' };
}

/** Envoi à signaler sur une ligne envoyée (comme l'ancienne liste) : à vérifier, en cours, correction à envoyer. */
export function alerteEnvoi(l) {
  if (!l) return null;
  if (l.statut === 'A_VERIFIER') return { txt: '⚠ envoi à vérifier', cls: 'warn' };
  if (l.statut === 'ENVOI_EN_COURS') return { txt: '⏳ envoi en cours', cls: 'part' };
  if (l.aCorriger) return { txt: '✉ correction à envoyer', cls: 'quai' };
  return null;
}

/** Facturation automatique à reprendre (bouton admin « Réessayer la facturation ») ? */
export const facturationAReprendre = (l) => !!(l && l.facturation && l.facturation.aReprendre);
export const texteFacturation = (l) => (facturationAReprendre(l) ? `Facturation automatique à reprendre : ${(l.facturation.motifs || []).join(' · ')}` : '');

/**
 * Rendez-vous d'enlèvement ACTIF d'une ligne (champ `rdv` : { jour, demiJournee, libelle, societe, vu, passe })
 * → { txt, cls, detail, titre } | null. Pastille « 📅 RDV lun. 19/10 matin » (rouge « passé » si le jour est
 * passé et la commande encore là) ; détail (2e ligne) « enlèvement : Transports Dupont » (null sans société).
 */
export function rdvVue(l) {
  const r = l && l.rdv;
  if (!r || typeof r.jour !== 'string' || !r.jour) return null;
  const dj = demiJournee(r.demiJournee);
  const quand = `${jourCourt(r.jour, 'fr')}${dj ? ` ${dj.motFr}` : ''}`;
  const societe = typeof r.societe === 'string' && r.societe.trim() ? r.societe.trim() : '';
  return {
    txt: `📅 RDV ${quand}${r.passe ? ' (passé)' : ''}`,
    cls: r.passe ? 'warn' : 'part',
    detail: societe ? `enlèvement : ${societe}` : null,
    titre: `Rendez-vous d'enlèvement : ${r.libelle || quand}${societe ? ` — ${societe}` : ''}${r.vu ? '' : ' (pas encore vu)'}`,
  };
}

/** Réponse du serveur → forme sûre (listes toujours présentes, compteurs numériques). */
export function lireReponseVue(r) {
  const res = { compteurs: {}, aFacturer: Array.isArray(r && r.aFacturer) ? r.aFacturer : [] };
  for (const v of VUES) {
    res[v] = Array.isArray(r && r[v]) ? r[v] : [];
    const n = r && r.compteurs ? Number(r.compteurs[v]) : NaN;
    res.compteurs[v] = Number.isFinite(n) ? n : res[v].length;
  }
  return res;
}

/** Une ligne disparaît de la vue (supprimée) : listes ET compteur mis à jour. */
export function retirerLigne(data, id) {
  if (!data) return data;
  const res = { ...data, compteurs: { ...data.compteurs }, aFacturer: (data.aFacturer || []).filter((l) => l.id !== id) };
  for (const v of VUES) {
    const avant = data[v] || [];
    res[v] = avant.filter((l) => l.id !== id);
    if (res[v].length !== avant.length) res.compteurs[v] = Math.max(0, (Number(data.compteurs[v]) || 0) - 1);
  }
  return res;
}
